// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
const REQUIRED_EXPORTS = [
    "resource_allocate",
    "resource_consume",
    "resource_is_live",
    "resource_live_count",
    "managed_create",
    "managed_release",
    "managed_is_released",
    "managed_use_and_release",
    "permit_create_boundary",
    "permit_grant",
    "permit_validate",
    "permit_revoke",
    "permit_revoke_boundary",
    "separate_as_data",
    "separate_as_instruction",
    "separate_safe_query",
    "origin_brand",
    "origin_verify",
    "origin_check_source",
    "reach_classify",
    "reach_declassify",
    "reach_level",
    "precommit_create",
    "precommit_commit",
    "precommit_cancel",
    "precommit_state",
    "precommit_description",
    "inv_builder_create",
    "inv_builder_add_check",
    "inv_establish",
    "inv_state_name",
    "inv_machine_create",
    "inv_machine_allow_transition",
    "inv_machine_transition",
    "inv_machine_current_state",
    "inv_machine_transition_count",
    "compose_register_operation",
    "compose_chain",
    "compose_validate_chain",
    "seam_descriptor",
    "seam_validate_snf_trace",
    "seam_define",
    "bridge_witness_mint",
    "bridge_propose",
    "bridge_admit",
    "bridge_admit_square",
    "bridge_receipt",
    "bridge_lifecycle",
    "bridge_witness_remaining_uses",
    "bridge_revoke_witness",
    "bridge_admit_status",
    "bridge_admit_square_status",
    "square_decide",
    "square_is_forbidden",
    "csk_version",
    "csk_available_theorems",
];
const DEFAULT_WASM_BINDGEN_MODULE = "../wasm/csk_wasm.js";
let wasmBindgenInstanceCounter = 0;
function assertNativeModule(module) {
    const missing = REQUIRED_EXPORTS.filter((name) => typeof module[name] !== "function");
    if (missing.length > 0) {
        throw new Error(`Native WASM module is missing required exports: ${missing.join(", ")}`);
    }
}
function assertNonNegativeInteger(value, name) {
    if (!Number.isInteger(value) || value < 0) {
        throw new Error(`${name} must be a non-negative integer`);
    }
    return value;
}
function stringifyPreparedQuery(value) {
    if (typeof value === "string") {
        return value;
    }
    return JSON.stringify(value);
}
function normalizeData(module, value) {
    return value.startsWith("DATA:") ? value : module.separate_as_data(value);
}
function normalizeInstruction(module, value) {
    return value.startsWith("INSTR:")
        ? value
        : module.separate_as_instruction(value);
}
export function isWasmRuntimeSupported() {
    return (typeof WebAssembly !== "undefined" &&
        typeof WebAssembly.instantiate === "function");
}
export function createPureTsBoundaryReceipt() {
    return {
        wasmSupported: isWasmRuntimeSupported(),
        nativeArtifactLoaded: false,
        adapterValidated: false,
        source: "pure-ts-fallback",
        detail: "No native artifact loaded; pure TypeScript provider passed the same WasmKernel contract.",
    };
}
export function createNativeBoundaryReceipt(source) {
    return {
        wasmSupported: isWasmRuntimeSupported(),
        nativeArtifactLoaded: true,
        adapterValidated: true,
        source,
        detail: "Native wasm-bindgen exports were adapted and passed the WasmKernel contract.",
    };
}
export function createNativeWasmKernel(module) {
    assertNativeModule(module);
    return {
        resource_allocate: module.resource_allocate,
        resource_consume: module.resource_consume,
        resource_is_live: module.resource_is_live,
        resource_live_count: module.resource_live_count,
        managed_create: module.managed_create,
        managed_release: module.managed_release,
        managed_is_released: module.managed_is_released,
        managed_use_and_release: module.managed_use_and_release,
        permit_create_boundary: module.permit_create_boundary,
        permit_grant: module.permit_grant,
        permit_validate: module.permit_validate,
        permit_revoke: module.permit_revoke,
        permit_revoke_boundary: module.permit_revoke_boundary,
        separate_as_data: module.separate_as_data,
        separate_as_instruction: module.separate_as_instruction,
        separate_safe_query(template, param) {
            return stringifyPreparedQuery(module.separate_safe_query(normalizeInstruction(module, template), normalizeData(module, param)));
        },
        origin_brand(value, label) {
            if (value.includes("⌘") || label.includes("⌘")) {
                throw new Error("DATA.ORIGIN value or label contains a forbidden delimiter");
            }
            return module.origin_brand(value, label);
        },
        origin_verify: module.origin_verify,
        origin_check_source: module.origin_check_source,
        reach_classify(value, level) {
            return module.reach_classify(value, assertNonNegativeInteger(level, "classification level"));
        },
        reach_declassify(branded, targetLevel) {
            return module.reach_declassify(branded, assertNonNegativeInteger(targetLevel, "target level"));
        },
        reach_level: module.reach_level,
        precommit_create: module.precommit_create,
        precommit_commit: module.precommit_commit,
        precommit_cancel: module.precommit_cancel,
        precommit_state: module.precommit_state,
        precommit_description: module.precommit_description,
        inv_builder_create: module.inv_builder_create,
        inv_builder_add_check: module.inv_builder_add_check,
        inv_establish: module.inv_establish,
        inv_state_name: module.inv_state_name,
        inv_machine_create: module.inv_machine_create,
        inv_machine_allow_transition: module.inv_machine_allow_transition,
        inv_machine_transition: module.inv_machine_transition,
        inv_machine_current_state: module.inv_machine_current_state,
        inv_machine_transition_count: module.inv_machine_transition_count,
        compose_register_operation: module.compose_register_operation,
        compose_chain: module.compose_chain,
        compose_validate_chain: module.compose_validate_chain,
        seam_descriptor: module.seam_descriptor,
        seam_validate_snf_trace: module.seam_validate_snf_trace,
        seam_define: module.seam_define,
        bridge_witness_mint: module.bridge_witness_mint,
        bridge_propose: module.bridge_propose,
        bridge_admit: module.bridge_admit,
        bridge_admit_square: module.bridge_admit_square,
        bridge_receipt: module.bridge_receipt,
        bridge_lifecycle: module.bridge_lifecycle,
        bridge_witness_remaining_uses: module.bridge_witness_remaining_uses,
        bridge_revoke_witness: module.bridge_revoke_witness,
        bridge_admit_status: module.bridge_admit_status,
        bridge_admit_square_status: module.bridge_admit_square_status,
        square_decide: module.square_decide,
        square_is_forbidden: module.square_is_forbidden,
        csk_version: module.csk_version,
        csk_available_theorems: module.csk_available_theorems,
    };
}
function isNodeRuntime() {
    const runtime = globalThis;
    return typeof runtime.process?.versions?.node === "string";
}
async function wasmInitInput(input) {
    const url = input instanceof URL ? input : new URL(input, import.meta.url);
    if (url.protocol !== "file:" || !isNodeRuntime())
        return url;
    const nodeFsPromises = "node:fs/promises";
    const { readFile } = (await import(/* @vite-ignore */ nodeFsPromises));
    return new Uint8Array(await readFile(url));
}
function resolveModuleUrl(input) {
    return input instanceof URL
        ? new URL(input.href)
        : new URL(input ?? DEFAULT_WASM_BINDGEN_MODULE, import.meta.url);
}
function freshImportUrl(moduleUrl) {
    const url = new URL(moduleUrl.href);
    wasmBindgenInstanceCounter += 1;
    url.searchParams.set("cskKernelInstance", wasmBindgenInstanceCounter.toString());
    return url;
}
async function importNativeModule(source) {
    if (typeof source === "object" &&
        !(source instanceof URL) &&
        source.module !== undefined) {
        return { module: source.module, source: "injected-module" };
    }
    const moduleUrl = typeof source === "object" && !(source instanceof URL)
        ? source.moduleUrl
        : source;
    const resolvedUrl = resolveModuleUrl(moduleUrl);
    const importUrl = freshImportUrl(resolvedUrl);
    const module = (await import(
    /* @vite-ignore */ importUrl.href));
    const wasmUrl = typeof source === "object" && !(source instanceof URL)
        ? source.wasmUrl
        : undefined;
    const defaultWasmUrl = wasmUrl ?? new URL("csk_wasm_bg.wasm", resolvedUrl);
    await module.default?.({
        module_or_path: await wasmInitInput(defaultWasmUrl),
    });
    return { module, source: "wasm-bindgen" };
}
export async function loadNativeWasmKernel(source) {
    const loaded = await importNativeModule(source);
    return {
        kernel: createNativeWasmKernel(loaded.module),
        boundary: createNativeBoundaryReceipt(loaded.source),
    };
}
//# sourceMappingURL=native-wasm.js.map